// A file's mark in Files (the Mac's FileMark and FileType, FilesPane.swift): a folder is the cmx folder
// (CmxFolder.swift, drawn small); a file a rounded tile in its kind's colour with a white glyph (dark on
// the light yellow and stone, as JavaScript's own logo is), PDF as letters. 18 beside a 14 name, as
// Drive's; it lays out at `size` so the columns keep their place.

import {
  IconAlignLeft,
  IconBraces,
  IconCode,
  IconFile,
  IconFileZip,
  IconLink,
  IconLock,
  IconMarkdown,
  IconMovie,
  IconMusic,
  IconPalette,
  IconPhoto,
  IconPresentation,
  IconSettings,
  IconTable,
  IconTerminal2,
} from '@tabler/icons-react';
import type { FileItem } from '../../net/files';
import type { TablerIcon } from '../ds';

type FileType = { glyph: TablerIcon; color: string; ink?: string; label?: string };

const DARK_INK = '#1C1917';

/** What a file is, by its extension: the glyph, the tile's colour and the glyph's. */
export function fileType(it: Pick<FileItem, 'kind' | 'name'>): FileType {
  if (it.kind === 'link') return { glyph: IconLink, color: '#78716C' };
  const name = it.name.toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1) : '';
  switch (ext) {
    case 'ts': case 'tsx': case 'mts': case 'cts': return { glyph: IconCode, color: '#3178C6' };
    case 'js': case 'mjs': case 'cjs': case 'jsx': return { glyph: IconCode, color: '#F0C419', ink: DARK_INK };
    case 'py': return { glyph: IconCode, color: '#3572A5' };
    case 'go': return { glyph: IconCode, color: '#00A7D0' };
    case 'rs': return { glyph: IconCode, color: '#CE422B' };
    case 'swift': return { glyph: IconCode, color: '#F05138' };
    case 'rb': return { glyph: IconCode, color: '#CC342D' };
    case 'html': case 'htm': case 'xml': case 'vue': case 'svelte': return { glyph: IconCode, color: '#E34F26' };
    case 'css': case 'scss': case 'sass': case 'less': return { glyph: IconPalette, color: '#7C3AED' };
    case 'sh': case 'bash': case 'zsh': case 'fish': return { glyph: IconTerminal2, color: '#292524' };
    case 'json': case 'jsonc': return { glyph: IconBraces, color: '#D97706' };
    case 'yaml': case 'yml': case 'toml': case 'ini': case 'cfg': case 'conf': case 'env': return { glyph: IconSettings, color: '#57534E' };
    case 'lock': return { glyph: IconLock, color: '#A8A29E', ink: DARK_INK };
    case 'md': case 'markdown': case 'mdx': return { glyph: IconMarkdown, color: '#475569' };
    case 'txt': case 'log': case 'rst': case 'doc': case 'docx': case 'rtf': case 'pages': return { glyph: IconAlignLeft, color: '#4285F4' };
    case 'csv': case 'tsv': case 'xls': case 'xlsx': case 'numbers': return { glyph: IconTable, color: '#0F9D58' };
    case 'ppt': case 'pptx': case 'key': return { glyph: IconPresentation, color: '#F4B400' };
    case 'pdf': return { glyph: IconFile, color: '#EA4335', label: 'PDF' };
    case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': case 'heic': case 'avif': case 'bmp': case 'tiff': case 'ico': case 'svg':
      return { glyph: IconPhoto, color: '#DB2777' };
    case 'mp4': case 'mov': case 'webm': case 'mkv': case 'avi': case 'm4v': return { glyph: IconMovie, color: '#DC2626' };
    case 'mp3': case 'wav': case 'm4a': case 'flac': case 'aac': case 'ogg': case 'aiff': return { glyph: IconMusic, color: '#EA580C' };
    case 'zip': case 'tar': case 'gz': case 'tgz': case 'bz2': case 'xz': case '7z': case 'rar': case 'dmg': return { glyph: IconFileZip, color: '#78716C' };
    default:
      // Makefile, Dockerfile, LICENSE, .gitignore: text, and stone
      return { glyph: !ext || name.startsWith('.') ? IconAlignLeft : IconFile, color: '#A8A29E', ink: DARK_INK };
  }
}

/** The cmx folder: its back with the tab, a frosted front over the lower part (the Figma's 120 box). */
function FolderMark({ size }: { size: number }) {
  return (
    <svg className="fl-folder" width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <path
        className="fl-folder-back"
        transform="translate(10 20)"
        d="M0 8C0 3.58 3.58 0 8 0H37.53C39.75 0 41.86 .92 43.37 2.53L46.13 5.47C47.64 7.08 49.75 8 51.97 8H92C96.42 8 100 11.58 100 16V72C100 76.42 96.42 80 92 80H8C3.58 80 0 76.42 0 72Z"
      />
      <rect className="fl-folder-front" x="10" y="40" width="100" height="60" rx="8" />
    </svg>
  );
}

export function FileMark({ item, size = 16 }: { item: Pick<FileItem, 'kind' | 'name'>; size?: number }) {
  if (item.kind === 'dir') return <FolderMark size={size + 2} />;
  const t = fileType(item);
  const tile = size + 2;
  const Glyph = t.glyph;
  return (
    <span
      className="fl-tile"
      aria-hidden="true"
      style={{ width: tile, height: tile, borderRadius: Math.max(4, tile * 0.24), background: t.color, color: t.ink ?? '#fff', fontSize: tile * 0.34 }}
    >
      {t.label ?? <Glyph size={tile * 0.64} stroke={2} />}
    </span>
  );
}
